import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  OrderingPlatformInput,
  OrderingPlatformType,
} from "__generated__/gql/graphql";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import Form, { FormProps } from "components/Shared/Forms/Form";
import { OptionalKeys, Writeable } from "utils/typescriptUtilis";
import * as orderingPlatformSchemaFields from "utils/schemas/orderingPlatform";
import {
  makePostgraphileErrorProcessor,
  getPostgraphileErrorMatchersForFields,
} from "utils/PostgraphileErrorExtensions";
import {
  ORDERING_PLATFORM_TYPE_LABELS,
  ORDERING_PLATFORM_TYPES,
} from "components/Shared/OrderingPlatformDirectory/OrderingPlatformTypeBadge";
import OrderingPlatformSelect from "components/Shared/Select/OrderingPlatformSelect";
import { Textarea } from "@chakra-ui/react";
import SwitchInput from "components/Shared/Forms/SwitchInput";
import UrlInput from "components/Shared/Forms/UrlInput";
import RadioOptionsSelect from "components/Shared/Forms/RadioOptionsSelect";
import RetailerSelect from "components/Shared/Select/RetailerSelect";
import MarkdownHelpText from "components/Shared/MarkdownHelpText";

export const orderingPlatformFields = [
  "name",
  "orderingPlatformType",
  "website",
  "description",
  "directRetailerId",
  "isParent",
  "parentId",
] as const;

type PrimitiveFields = OptionalKeys<
  Required<
    Writeable<
      Pick<OrderingPlatformInput, (typeof orderingPlatformFields)[number]>
    >
  >,
  "website" | "description" | "directRetailerId" | "isParent" | "parentId"
>;

export type OrderingPlatformFormFields = PrimitiveFields;

const schema: yup.ObjectSchema<OrderingPlatformFormFields> = yup
  .object()
  .shape({
    name: orderingPlatformSchemaFields.name,
    orderingPlatformType: orderingPlatformSchemaFields.orderingPlatformType,
    website: orderingPlatformSchemaFields.website,
    description: orderingPlatformSchemaFields.description,
    directRetailerId: orderingPlatformSchemaFields.directRetailerId,
    isParent: orderingPlatformSchemaFields.isParent,
    parentId: orderingPlatformSchemaFields.parentId,
  });

const { FormField, ControlledFormField } =
  createFormFieldComponents<OrderingPlatformFormFields>({
    schema,
  });

export const processApolloError =
  makePostgraphileErrorProcessor<PrimitiveFields>(
    getPostgraphileErrorMatchersForFields<keyof PrimitiveFields>(
      orderingPlatformFields,
      {
        type: "OrderingPlatform",
      },
    ),
  );

export type OrderingPlatformFormProps = Pick<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  FormProps<OrderingPlatformFormFields, any>,
  "onSubmit" | "onValidSubmit"
> & {
  defaultValues?: Partial<OrderingPlatformFormFields>;
  formId?: string;
  isAdmin?: boolean;
  isLoaded?: boolean;
};

export default function OrderingPlatformForm({
  defaultValues,
  formId = "ordering-platform-form",
  isAdmin = false,
  isLoaded,
  onSubmit,
  onValidSubmit,
}: OrderingPlatformFormProps) {
  const formContext = useForm<OrderingPlatformFormFields>({
    defaultValues: { name: "" },
    resolver: yupResolver(schema),
  });

  const { reset, watch } = formContext;

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  const [orderingPlatformType, isParent] = watch([
    "orderingPlatformType",
    "isParent",
  ]);

  return (
    <Form {...{ formContext, onSubmit, onValidSubmit, isLoaded }} id={formId}>
      <FormField name="name" autoFocus />

      <ControlledFormField
        name="orderingPlatformType"
        onChange={(evt) => {
          const newType = (evt.target as unknown as HTMLInputElement)
            .value as OrderingPlatformType;
          if (newType !== OrderingPlatformType.DIRECT_RETAILER) {
            formContext.setValue("directRetailerId", null);
          }
        }}
        renderInput={({ field }) => (
          <RadioOptionsSelect
            options={ORDERING_PLATFORM_TYPES}
            labels={ORDERING_PLATFORM_TYPE_LABELS}
            {...field}
          />
        )}
      />

      <ControlledFormField
        name="directRetailerId"
        isDisabled={
          orderingPlatformType !== OrderingPlatformType.DIRECT_RETAILER
        }
        renderInput={({ field }) => (
          <RetailerSelect
            {...field}
            isAdmin={isAdmin}
            isDisabled={
              orderingPlatformType !== OrderingPlatformType.DIRECT_RETAILER
            }
            isClearable
          />
        )}
      />

      <ControlledFormField
        name="website"
        renderInput={({ field }) => <UrlInput {...field} />}
      />

      <FormField
        name="description"
        InputComponent={Textarea}
        helpText={<MarkdownHelpText />}
      />

      {isAdmin && (
        <>
          <ControlledFormField
            name="isParent"
            onChange={(evt) => {
              if ((evt.target as HTMLInputElement).checked) {
                formContext.setValue("parentId", null);
              }
            }}
            renderInput={({ field }) => <SwitchInput {...field} />}
          />

          <ControlledFormField
            name="parentId"
            isDisabled={!!isParent}
            renderInput={({ field }) => (
              <OrderingPlatformSelect
                {...field}
                isAddedToBrand={!isAdmin}
                isAdmin={isAdmin}
                isClearable
                isDisabled={!!isParent}
                filter={{ isParent: { equalTo: true } }}
              />
            )}
          />
        </>
      )}
    </Form>
  );
}
